import { serializeParams } from "@/utils/apiUtils";
import { AxiosPromise } from "axios";
import { ApiRequestAdapter } from "../../common/api/ApiRequestAdapter";
import { apiService } from "../../common/api/apiService";
import { HttpContentType, HttpHeaders, HttpMethod } from "../../common/api/http";
import { PageSizes } from "../../common/constants";
import { OrderDirection } from "../../common/enums";
import {
  EntityIdObject,
  EntityObject,
  PageRequest,
  PageResult,
  ThreeLevelEntityIdObject,
  TwoLevelEntityIdObject,
  TwoLevelEntityObject,
} from "../../common/types";
import { ContractAttachment } from "../contract/types";
import {
  AgentCreateUserWithClientUserAccount,
  AgentUpdateClientUserAccountWithUser,
  ClientUserAccountWithUserAgentView,
} from "../user/types";
import { ClientOrderByColumn } from "./enums";
import {
  Client,
  ClientAgreement,
  ClientAttachment,
  ClientFilterExportRequest,
  ClientFilterPageRequest,
  ClientFilterPageResult,
  ClientPartnerAgent,
  ClientSearchRequest,
  ClientSearchResult,
  CreateUpdateClient,
  CreateUpdateClientAgreement,
  MarkClientAttachmentType,
  UpdateClientAssignedAgents,
  ValidateContractClientRequest,
  ValidateContractClientsRequest,
} from "./types";

const FILTER_CLIENTS = ApiRequestAdapter.create("/clients", HttpMethod.GET);
const IMPORT_CLIENTS = ApiRequestAdapter.create("/clients/import", HttpMethod.POST);
const DOWNLOAD_CLIENTS_IMPORT_RESULT = ApiRequestAdapter.create("/clients/import/{0}", HttpMethod.GET);
const DOWNLOAD_CLIENTS_EXPORT = ApiRequestAdapter.create("/clients/export", HttpMethod.GET);
const SEARCH_CLIENT = ApiRequestAdapter.create("/clients/search", HttpMethod.GET);

const VALIDATE_CLIENT = ApiRequestAdapter.create("/clients/validate", HttpMethod.POST);
const VALIDATE_CLIENTS = ApiRequestAdapter.create("/clients/validate-all", HttpMethod.POST);

const GET_CLIENT = ApiRequestAdapter.create("/clients/{0}", HttpMethod.GET);
const UPDATE_CLIENT = ApiRequestAdapter.create("/clients/{0}", HttpMethod.PUT);
const UPDATE_CLIENT_ASSIGNED_AGENTS = ApiRequestAdapter.create("/clients/{0}/assigned-agents", HttpMethod.PUT);
const CREATE_CLIENT = ApiRequestAdapter.create("/clients", HttpMethod.POST);
const DELETE_CLIENT = ApiRequestAdapter.create("/clients/{0}", HttpMethod.DELETE);
const GET_CLIENT_PARTNER_AGENT = ApiRequestAdapter.create("/clients/{0}/partner-agent", HttpMethod.GET);

const GET_CLIENT_USER_ACCOUNTS = ApiRequestAdapter.create("/clients/{0}/accounts", HttpMethod.GET);
const CREATE_USER_WITH_CLIENT_USER_ACCOUNT = ApiRequestAdapter.create("/clients/{0}/accounts", HttpMethod.POST);
const RESEND_USER_CONFIRM_LINK = ApiRequestAdapter.create(
  "/clients/{0}/accounts/{1}/resend-confirm-link",
  HttpMethod.PUT,
);
const UPDATE_CLIENT_USER_ACCOUNT = ApiRequestAdapter.create("/clients/{0}/accounts/{1}", HttpMethod.PUT);
const DELETE_CLIENT_USER_ACCOUNT = ApiRequestAdapter.create("/clients/{0}/accounts/{1}", HttpMethod.DELETE);

const GET_CLIENT_ATTACHMENTS = ApiRequestAdapter.create("/clients/{0}/attachments", HttpMethod.GET);
const UPLOAD_CLIENT_ATTACHMENTS = ApiRequestAdapter.create("/clients/{0}/attachments", HttpMethod.POST);
const DOWNLOAD_CLIENT_ATTACHMENT = ApiRequestAdapter.create("/clients/{0}/attachments/{1}", HttpMethod.GET);
const DOWNLOAD_CLIENT_ATTACHMENTS_AS_ZIP = ApiRequestAdapter.create("/clients/{0}/attachments/all", HttpMethod.GET);
const DELETE_CLIENT_ATTACHMENT = ApiRequestAdapter.create("/clients/{0}/attachments/{1}", HttpMethod.DELETE);
const MARK_CLIENT_ATTACHMENT_TYPE = ApiRequestAdapter.create("/clients/{0}/attachments/{1}", HttpMethod.PUT);

const FILTER_CLIENT_AGREEMENTS = ApiRequestAdapter.create("/clients/{0}/agreements", HttpMethod.GET);
const CREATE_CLIENT_AGREEMENT = ApiRequestAdapter.create("/clients/{0}/agreements", HttpMethod.POST);
const UPDATE_CLIENT_AGREEMENT = ApiRequestAdapter.create("/clients/{0}/agreements/{1}", HttpMethod.PUT);
const DELETE_CLIENT_AGREEMENT = ApiRequestAdapter.create("/clients/{0}/agreements/{1}", HttpMethod.DELETE);

const ASSIGN_CLIENT_ATTACHMENT_TO_CONTRACT = ApiRequestAdapter.create(
  "/clients/{0}/attachments/{1}/contracts/{2}/assign",
  HttpMethod.POST,
);

export const clientApi = {
  filterClients: (request: ClientFilterPageRequest): AxiosPromise<ClientFilterPageResult> => {
    return apiService.get(FILTER_CLIENTS.url, { params: request, paramsSerializer: { serialize: serializeParams } });
  },
  importClients: (request: FormData): AxiosPromise<number> => {
    return apiService.post(IMPORT_CLIENTS.url, request, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
    });
  },
  downloadClientsImportResult: (request: EntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_CLIENTS_IMPORT_RESULT.getParametrizedUrl(request.id), { responseType: "blob" });
  },
  downloadClientsExport: (request: ClientFilterExportRequest): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_CLIENTS_EXPORT.url, {
      params: request,
      paramsSerializer: { serialize: serializeParams },
      responseType: "blob",
    });
  },
  searchClient: (request: ClientSearchRequest): AxiosPromise<ClientSearchResult> => {
    return apiService.get(SEARCH_CLIENT.url, { params: request });
  },
  validateClient: ({ validationGroup, ...request }: ValidateContractClientRequest): AxiosPromise => {
    return apiService.post(VALIDATE_CLIENT.url, request, { params: { validationGroup: validationGroup } });
  },
  validateClients: ({ validationGroup, ...request }: ValidateContractClientsRequest): AxiosPromise => {
    return apiService.post(VALIDATE_CLIENTS.url, request, { params: { validationGroup: validationGroup } });
  },
  getClient: (request: EntityIdObject): AxiosPromise<Client> => {
    return apiService.get(GET_CLIENT.getParametrizedUrl(request.id));
  },
  updateClient: (request: EntityObject<CreateUpdateClient>): AxiosPromise<Client> => {
    return apiService.put(UPDATE_CLIENT.getParametrizedUrl(request.id), request.object);
  },
  updateClientAssignedAgents: (request: EntityObject<UpdateClientAssignedAgents>): AxiosPromise<Client> => {
    return apiService.put(UPDATE_CLIENT_ASSIGNED_AGENTS.getParametrizedUrl(request.id), request.object);
  },
  createClient: (request: CreateUpdateClient): AxiosPromise<Client> => {
    return apiService.post(CREATE_CLIENT.url, request);
  },
  deleteClient: (request: EntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_CLIENT.getParametrizedUrl(request.id));
  },
  getClientPartnerAgent: (request: EntityIdObject): AxiosPromise<ClientPartnerAgent> => {
    return apiService.get(GET_CLIENT_PARTNER_AGENT.getParametrizedUrl(request.id));
  },
  getClientUserAccounts: (request: EntityIdObject): AxiosPromise<ClientUserAccountWithUserAgentView[]> => {
    return apiService.get(GET_CLIENT_USER_ACCOUNTS.getParametrizedUrl(request.id));
  },
  createUserWithClientUserAccount: (
    request: EntityObject<AgentCreateUserWithClientUserAccount>,
  ): AxiosPromise<ClientUserAccountWithUserAgentView> => {
    return apiService.post(CREATE_USER_WITH_CLIENT_USER_ACCOUNT.getParametrizedUrl(request.id), request.object);
  },
  resendUserConfirmLink: (request: TwoLevelEntityIdObject): AxiosPromise<ClientUserAccountWithUserAgentView> => {
    return apiService.put(RESEND_USER_CONFIRM_LINK.getParametrizedUrl(request.id1, request.id2));
  },
  updateClientUserAccount: (
    request: TwoLevelEntityObject<AgentUpdateClientUserAccountWithUser>,
  ): AxiosPromise<ClientUserAccountWithUserAgentView> => {
    return apiService.put(UPDATE_CLIENT_USER_ACCOUNT.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteClientUserAccount: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_CLIENT_USER_ACCOUNT.getParametrizedUrl(request.id1, request.id2));
  },
  getClientAttachments: (request: EntityIdObject): AxiosPromise<ClientAttachment[]> => {
    return apiService.get(GET_CLIENT_ATTACHMENTS.getParametrizedUrl(request.id));
  },
  uploadClientAttachments: (request: EntityObject<FormData>): AxiosPromise<ClientAttachment[]> => {
    return apiService.post(UPLOAD_CLIENT_ATTACHMENTS.getParametrizedUrl(request.id), request.object, {
      headers: { [HttpHeaders.CONTENT_TYPE]: HttpContentType.MULTIPART_FORM_DATA },
    });
  },
  downloadClientAttachment: (request: TwoLevelEntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_CLIENT_ATTACHMENT.getParametrizedUrl(request.id1, request.id2), {
      responseType: "blob",
    });
  },
  downloadClientAttachmentsAsZip: (request: EntityIdObject): AxiosPromise<Blob> => {
    return apiService.get(DOWNLOAD_CLIENT_ATTACHMENTS_AS_ZIP.getParametrizedUrl(request.id), {
      responseType: "blob",
    });
  },
  deleteClientAttachment: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_CLIENT_ATTACHMENT.getParametrizedUrl(request.id1, request.id2));
  },
  markClientAttachmentType: (
    request: TwoLevelEntityObject<MarkClientAttachmentType>,
  ): AxiosPromise<ClientAttachment> => {
    return apiService.put(MARK_CLIENT_ATTACHMENT_TYPE.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  filterClientAgreements: (request: EntityObject<PageRequest>): AxiosPromise<PageResult<ClientAgreement>> => {
    return apiService.get(FILTER_CLIENT_AGREEMENTS.getParametrizedUrl(request.id), {
      params: request.object,
      paramsSerializer: { serialize: serializeParams },
    });
  },
  createClientAgreement: (request: EntityObject<CreateUpdateClientAgreement>): AxiosPromise<ClientAgreement> => {
    return apiService.post(CREATE_CLIENT_AGREEMENT.getParametrizedUrl(request.id), request.object);
  },
  updateClientAgreement: (
    request: TwoLevelEntityObject<CreateUpdateClientAgreement>,
  ): AxiosPromise<ClientAgreement> => {
    return apiService.put(UPDATE_CLIENT_AGREEMENT.getParametrizedUrl(request.id1, request.id2), request.object);
  },
  deleteClientAgreement: (request: TwoLevelEntityIdObject): AxiosPromise => {
    return apiService.delete(DELETE_CLIENT_AGREEMENT.getParametrizedUrl(request.id1, request.id2));
  },
  assignAttachmentToContract: (request: ThreeLevelEntityIdObject): AxiosPromise<ContractAttachment> => {
    return apiService.post(
      ASSIGN_CLIENT_ATTACHMENT_TO_CONTRACT.getParametrizedUrl(request.id1, request.id2, request.id3),
    );
  },
};

export const DEFAULT_FILTER_CLIENTS_QUERY_PARAMS: Required<
  Omit<
    ClientFilterPageRequest,
    "contactClientDaysMin" | "contactClientDaysMax" | "hasMobileApp" | "premiumType" | "hasPartnerAgent"
  >
> = {
  pageIndex: 0,
  pageSize: PageSizes.LARGE,
  keyword: "",
  orderBy: [ClientOrderByColumn.CREATED_AT],
  orderDirections: [OrderDirection.DESC],
  types: [],
  createdAtMin: "",
  createdAtMax: "",
  managedByIds: [],
  managedByDirectorateIds: [],
  partnerAgentDirectorateIds: [],
  partnerAgentIdNumber: "",
};
